import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';

interface ChangeContributionDaySheetProps {
	open: boolean;
	currentDay: number;
	onClose: () => void;
	onConfirm: (day: number) => Promise<void>;
	translate: (key: string) => string;
	langCode?: string;
}

// Rango 1-28: excluimos 29/30/31 para evitar saltos en febrero y meses cortos.
// Alineado con el DTO del backend (`UpdateB2CContributionDayInputDto`).
const DAYS = Array.from({ length: 28 }, (_, i) => i + 1);

const ChangeContributionDaySheet: React.FC<ChangeContributionDaySheetProps> = ({
	open,
	currentDay,
	onClose,
	onConfirm,
	translate,
	langCode = 'es'
}) => {
	const [selectedDay, setSelectedDay] = useState(currentDay);
	const [hasTouched, setHasTouched] = useState(false);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (open) {
			setSelectedDay(currentDay);
			setHasTouched(false);
		}
	}, [open, currentDay]);

	const todayOfMonth = new Date().getDate();
	const appliesNextMonth = selectedDay <= todayOfMonth;

	const dateLocale = langCode === 'en' ? 'en-GB' : 'es-ES';
	const nextMonthName = new Date(
		new Date().getFullYear(),
		new Date().getMonth() + 1,
		1
	).toLocaleDateString(dateLocale, { month: 'long' });

	const thisMonthName = new Date().toLocaleDateString(dateLocale, { month: 'long' });

	const handleConfirm = async () => {
		setLoading(true);
		try {
			await onConfirm(selectedDay);
			onClose();
		} catch {
			// El caller maneja el toast/feedback de error. El modal se queda abierto
			// para que el usuario pueda reintentar o cancelar.
		} finally {
			setLoading(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={(v) => !v && onClose()}>
			<DialogContent className="max-w-md">
				<DialogHeader className="pb-2">
					<DialogTitle className="text-xl">
						{translate('individual.changeContributionDay.title') || 'Día de aportación periódica'}
					</DialogTitle>
					<DialogDescription className="text-sm">
						{translate('individual.changeContributionDay.subtitle') ||
							'Elige el día del mes en que se realizará tu aportación automática.'}
					</DialogDescription>
					<p className="text-xs text-muted-foreground pt-1">
						{translate('individual.changeContributionDay.businessDayNote') ||
							'Si el día de aportación cae en festivo o fin de semana, se realizará el siguiente día hábil.'}
					</p>
				</DialogHeader>

				<div className="py-3">
					<select
						value={selectedDay}
						onChange={(e) => {
							setSelectedDay(Number(e.target.value));
							setHasTouched(true);
						}}
						className="input-arca w-full text-base"
					>
						{DAYS.map((d) => (
							<option key={d} value={d}>
								{d}
							</option>
						))}
					</select>
				</div>

				{/* Aviso principal sobre cuándo aplica — solo si el usuario ha tocado el select */}
				{hasTouched && (
					<p className="text-sm text-foreground leading-relaxed">
						{appliesNextMonth ? (
							<>
								{(
									translate('individual.changeContributionDay.hintNextMonthPart1') ||
									'A partir de {month}, tus aportaciones mensuales se realizarán el día {day}. '
								)
									.replace('{day}', String(selectedDay))
									.replace('{month}', nextMonthName)}
								<strong>
									{translate('individual.changeContributionDay.hintNextMonthBold') ||
										'Este mes no se realizará ninguna aportación.'}
								</strong>{' '}
								{(
									translate('individual.changeContributionDay.hintNextMonthPart2') ||
									'Si quieres aportar en {currentMonth}, espera a después del día {currentDay} para hacer el cambio.'
								)
									.replace('{currentMonth}', thisMonthName)
									.replace('{currentDay}', String(currentDay))}
							</>
						) : (
							(
								translate('individual.changeContributionDay.hintThisMonth') ||
								'A partir de este mes, tus aportaciones mensuales se realizarán el día {day} de cada mes.'
							).replace('{day}', String(selectedDay))
						)}
					</p>
				)}

				<div className="flex gap-3 pt-2">
					<Button variant="outline" className="flex-1" onClick={onClose} disabled={loading}>
						{translate('individual.changeContributionDay.cancel') || 'Cancelar'}
					</Button>
					<Button
						className="flex-1"
						onClick={handleConfirm}
						disabled={loading || selectedDay === currentDay}
					>
						{loading ? (
							<Loader2 className="w-4 h-4 animate-spin" />
						) : (
							translate('individual.changeContributionDay.confirm') || 'Confirmar'
						)}
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default ChangeContributionDaySheet;
